import React, {useEffect, useState} from 'react';
import {Box, Typography, Collapse, IconButton} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './ArrCollapsiblePanel.scss';
import ArrTooltip from '../arr-tooltip/ArrTooltip';

export interface ArrCollapsiblePanelProps {
  title: string | React.ReactElement;
  isExpanded?: boolean;
  onToggle: (expanded: boolean) => void;
  content: string | React.ReactElement;
  isDisabled?: boolean;
  tooltipText?: string;
  className?: string;
}

const ArrCollapsiblePanel: React.FC<ArrCollapsiblePanelProps> = ({
  title,
  isExpanded = true,
  onToggle,
  content,
  isDisabled = false,
  tooltipText = '',
  className = '',
}) => {
  const [expanded, setExpanded] = useState(isExpanded);

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const handleToggle = () => {
    if (isDisabled) return;
    const newState = !expanded;
    setExpanded(newState);
    onToggle(newState);
  };

  return (
    <Box
      className={`arr-collapsible-panel ${isDisabled ? 'disabled' : ''} ${className}`}
      data-testid="arr-collapsible-panel"
    >
      <Box
        className="panel-header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-disabled={isDisabled}
        data-testid="panel-header"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <Box className="panel-title" data-testid="panel-title">
          <Typography
            variant="subtitle1"
            className="title-label"
            data-testid="panel-title-label"
          >
            {title}
          </Typography>

          {tooltipText && !isDisabled && (
            <ArrTooltip
              content={tooltipText}
              labelId={`tooltip-${tooltipText}`}
            >
              <IconButton
                aria-label="Info"
                size="small"
                className="info-button"
                data-testid="info-button"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </ArrTooltip>
          )}
        </Box>

        <Box className="toggle-indicator" data-testid="toggle-indicator">
          <Typography
            variant="body2"
            className="toggle-label"
            data-testid="toggle-label"
          >
            {expanded ? 'Hide' : 'Show'}
          </Typography>
          {expanded ? (
            <ExpandLessIcon data-testid="icon-less" />
          ) : (
            <ExpandMoreIcon data-testid="icon-more" />
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout={300}>
        <Box className="panel-content" data-testid="panel-content">
          {content}
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(ArrCollapsiblePanel);
